import { Archive, Download, Forward, Reply, ReplyAll, Star, Tag, Trash2 } from "lucide-react";
import type { JSX } from "react";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Separator,
  Skeleton,
} from "@mailflare/ui";
import type { Label, LabelId, MessageDetail, MessageId } from "@mailflare/shared";

import { API_BASE_URL } from "@/lib/api-client";
import { formatFullDate, formatAddress, initialsFor } from "@/lib/utils";

import { AttachmentList } from "./attachment-list";
import { MessageHtml } from "./message-html";

export interface ReadingPaneProps {
  readonly messageId: MessageId | null;
  readonly detail: MessageDetail | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly labels: readonly Label[];
  readonly onRetry: () => void;
  readonly onClose: () => void;
  readonly onReply: (mode: "reply" | "reply_all" | "forward") => void;
  readonly onArchive: () => void;
  readonly onTrash: () => void;
  readonly onToggleFlag: () => void;
  readonly onToggleLabel: (labelId: LabelId, present: boolean) => void;
}

function SpamBadge({ detail }: { readonly detail: MessageDetail }): JSX.Element | null {
  if (detail.spamVerdict === "spam" || detail.spamVerdict === "phishing") {
    return <Badge variant="warning">Suspected spam</Badge>;
  }
  if (detail.spamVerdict === "suspect") {
    return <Badge variant="warning">Possibly spam</Badge>;
  }
  return null;
}

function AuthBadge({ detail }: { readonly detail: MessageDetail }): JSX.Element | null {
  if (detail.auth === null) return null;
  const { dmarc, spf, dkim } = detail.auth;
  const variant = dmarc === "pass" ? "secondary" : dmarc === "fail" ? "destructive" : "outline";
  return (
    <Badge variant={variant} title={`SPF ${spf}, DKIM ${dkim}, DMARC ${dmarc}`}>
      DMARC {dmarc}
    </Badge>
  );
}

export function ReadingPane({
  messageId,
  detail,
  isLoading,
  isError,
  labels,
  onRetry,
  onClose,
  onReply,
  onArchive,
  onTrash,
  onToggleFlag,
  onToggleLabel,
}: ReadingPaneProps): JSX.Element {
  if (messageId === null) {
    return (
      <EmptyState
        title="No message selected"
        description="Choose a message from the list to read it here."
        className="m-auto"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-4" aria-busy="true" aria-label="Loading message">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || detail === undefined) {
    return (
      <ErrorState
        message="This message could not be loaded."
        onRetry={onRetry}
        className="m-auto"
      />
    );
  }

  const rawUrl = `${API_BASE_URL}/messages/${encodeURIComponent(detail.id)}/raw`;

  return (
    <article aria-label="Message" className="flex min-h-0 flex-1 flex-col">
      <header className="border-border flex flex-wrap items-center gap-1 border-b p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onReply("reply")}
          data-testid="reply-button"
        >
          <Reply aria-hidden className="size-4" />
          Reply
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onReply("reply_all")}>
          <ReplyAll aria-hidden className="size-4" />
          Reply all
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onReply("forward")}>
          <Forward aria-hidden className="size-4" />
          Forward
        </Button>
        <Separator orientation="vertical" className="mx-1 hidden h-5 sm:block" />
        <Button variant="ghost" size="sm" onClick={onArchive}>
          <Archive aria-hidden className="size-4" />
          Archive
        </Button>
        <Button variant="ghost" size="sm" onClick={onTrash}>
          <Trash2 aria-hidden className="size-4" />
          Delete
        </Button>
        <Button variant="ghost" size="sm" onClick={onToggleFlag} aria-pressed={detail.isFlagged}>
          <Star
            aria-hidden
            className={detail.isFlagged ? "fill-warning text-warning size-4" : "size-4"}
          />
          {detail.isFlagged ? "Unstar" : "Star"}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <Tag aria-hidden className="size-4" />
              Labels
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52">
            <DropdownMenuLabel>Label this message</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {labels.length === 0 ? (
              <p className="text-muted-foreground px-2 py-1.5 text-sm">No labels yet</p>
            ) : (
              labels.map((label) => {
                const present = detail.labelIds.includes(label.id);
                return (
                  <DropdownMenuCheckboxItem
                    key={label.id}
                    checked={present}
                    onCheckedChange={(checked) => onToggleLabel(label.id, checked === true)}
                  >
                    {label.name}
                  </DropdownMenuCheckboxItem>
                );
              })
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button asChild variant="ghost" size="sm" className="ml-auto">
          <a href={rawUrl} download={`${detail.id}.eml`} rel="noopener noreferrer">
            <Download aria-hidden className="size-4" />
            Raw
          </a>
        </Button>
        <Button variant="ghost" size="sm" className="lg:hidden" onClick={onClose}>
          Close
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-5 sm:px-6">
          <div className="flex items-start gap-3">
            <Avatar className="size-10">
              <AvatarFallback>{initialsFor(detail.from)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-lg font-semibold">{detail.subject || "(no subject)"}</h2>
              <p className="text-sm">
                <span className="font-medium">{detail.from.name ?? detail.from.address}</span>{" "}
                <span className="text-muted-foreground">&lt;{detail.from.address}&gt;</span>
              </p>
              <p className="text-muted-foreground truncate text-xs">
                to {detail.to.map((address) => address.address).join(", ")}
                {detail.cc.length > 0
                  ? ` · cc ${detail.cc.map((address) => address.address).join(", ")}`
                  : ""}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <time className="text-muted-foreground text-xs" dateTime={detail.receivedAt}>
                {formatFullDate(detail.receivedAt)}
              </time>
              <div className="flex gap-1">
                <SpamBadge detail={detail} />
                <AuthBadge detail={detail} />
              </div>
            </div>
          </div>

          {(detail.replyTo !== null || detail.listUnsubscribe !== null) && (
            <p className="text-muted-foreground mt-3 text-xs">
              {detail.replyTo !== null ? `Reply-To: ${formatAddress(detail.replyTo)}` : ""}
              {detail.listUnsubscribe !== null
                ? " · This message offers an unsubscribe option."
                : ""}
            </p>
          )}

          <Separator className="my-4" />
          <MessageHtml html={detail.body.html} />
          {detail.body.html === null && detail.body.text.length > 0 ? (
            <pre className="text-sm whitespace-pre-wrap">{detail.body.text}</pre>
          ) : null}
          <AttachmentList attachments={detail.attachments} />
        </div>
      </div>
    </article>
  );
}
